/**
 * Profile Photo Manager
 *
 * Single place to manage profile photo state:
 * - Stores per-user photo entry (url, keyBase64, cachePath, updatedAt)
 * - Hydrates cached decrypted files on startup
 * - Caches decrypted blobs locally and updates store
 * - Builds fanout payloads for contacts
 *
 * Rendering should always prefer cachePath (local decrypted) and never fetch/decrypt in the component.
 */

import { useStore, parseJwt } from "@/state/legacy_module_api";
import { crypto as coreCrypto, decodeBase64 } from "@/core_bridge/module_api";
import { displayPhotoCachePaths } from "@/core_bridge/module_api";
import { db, filesystem } from "@/services";
import { saveProfilePhotoEntry } from "./effects/db/storage";

export interface ProfilePhotoEntry {
  userId: string;
  url: string;
  keyBase64?: string;
  cachePath?: string;
  updatedAt?: number;
}

// @platform-only: web fetches and decrypts profile-photo blobs into the browser cache for rendering.
export async function ensureProfilePhotoCached(entry: ProfilePhotoEntry): Promise<string | undefined> {
  if (!entry.url || !entry.keyBase64 || entry.cachePath) {
    return entry.cachePath;
  }

  const response = await fetch(entry.url);
  if (!response.ok) {
    throw new Error(`Failed to fetch profile photo: ${response.status}`);
  }

  const encryptedData = new Uint8Array(await response.arrayBuffer());
  if (encryptedData.length < 24) {
    throw new Error("Encrypted profile photo payload too short");
  }

  const nonce = encryptedData.slice(0, 24);
  const ciphertext = encryptedData.slice(24);
  const key = decodeBase64(entry.keyBase64);
  const decrypted = coreCrypto.secretboxOpen(ciphertext, nonce, key);

  return cacheDecryptedPhoto({
    userId: entry.userId,
    url: entry.url,
    keyBase64: entry.keyBase64,
    blob: new Blob([decrypted], { type: "image/jpeg" }),
  });
}

const getStore = () => useStore.getState();

/** Get current user's ID from JWT token */
function getMyUserId(): string | undefined {
  const state = getStore();
  if (!state.apiToken) return undefined;
  return parseJwt(state.apiToken).user_id;
}

/**
 * Save decrypted blob locally and update store entry
 */
export async function cacheDecryptedPhoto({
  userId,
  url,
  keyBase64,
  blob,
}: {
  userId: string;
  url: string;
  keyBase64?: string;
  blob: Blob;
}): Promise<string> {
  let cachePath = `profilephoto/${userId}/decrypted`;
  try {
    const paths = displayPhotoCachePaths(userId, url) as any;
    cachePath = paths?.user_cache_path || cachePath;
  } catch (err) {
    console.warn("[profilePhotoManager] cache path fallback", err);
  }
  await filesystem.writeFile({ path: cachePath, blob });

  const setProfilePhoto = (useStore.getState() as any).setProfilePhoto;
  if (setProfilePhoto) {
    const entry = {
      userId,
      url,
      keyBase64,
      cachePath,
      updatedAt: Date.now(),
    };
    setProfilePhoto(entry);
    try {
      await saveProfilePhotoEntry(entry);
    } catch (err) {
      console.warn("[profilePhotoManager] failed to persist profile photo entry", err);
    }
  }
  // Update self references if it's me
  if (userId === getMyUserId()) {
    getStore().setMyProfilePhoto?.(cachePath);
    const me = getStore().contactsById?.[userId];
    if (me && getStore().setUser) {
      getStore().setUser({ ...me, displayPhoto: cachePath } as any);
      // Persist contact update so reloads keep cachePath
      try {
        await db.updateUser(userId, { displayPhoto: cachePath } as any);
      } catch {
        // best effort
      }
    }
  } else {
    const contact = getStore().contactsById?.[userId];
    if (contact && getStore().setUser) {
      getStore().setUser({ ...contact, displayPhoto: cachePath } as any);
      try {
        await db.updateUser(userId, { displayPhoto: cachePath } as any);
      } catch {
        // best effort for contact persistence
      }
    }
  }
  return cachePath;
}

/**
 * Hydrate from stored profilePhotos: if url+key exist but cachePath missing, schedule decrypt/caching.
 * NOTE: decryption/fetching should be done by caller; this helper just updates store when you have the blob.
 */
export function hydrateProfilePhotoFromCache(userId: string) {
  const entry = (getStore() as any).profilePhotos?.[userId];
  if (entry?.cachePath) {
    return entry.cachePath;
  }
  return undefined;
}

/**
 * Remove photo entry and cached files for a user
 */
export async function clearProfilePhoto(userId: string) {
  const cachePath = (getStore() as any).profilePhotos?.[userId]?.cachePath;
  const removeProfilePhoto = (getStore() as any).removeProfilePhoto;
  if (cachePath) {
    try {
      await filesystem.deleteFile?.(cachePath);
    } catch {
      // ignore
    }
  }
  removeProfilePhoto?.(userId);
  if (userId === getMyUserId()) {
    getStore().setMyProfilePhoto?.(undefined as any);
    const me = getStore().contactsById?.[userId];
    if (me && getStore().setUser) {
      getStore().setUser({ ...me, displayPhoto: "" } as any);
    }
  }
}

/**
 * Build payload to send to a contact with your display photo key/url
 */
export function buildDisplayPhotoFanoutPayload() {
  const myUserId = getMyUserId();
  const entry = (getStore() as any).profilePhotos?.[myUserId || ""];
  if (!myUserId || !entry?.url || !getStore().displayPhotoKey) return null;
  return {
    userId: myUserId,
    displayPhotoUrl: entry.url,
    displayPhotoKey: getStore().displayPhotoKey,
  };
}
